import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { compareVersions } from 'compare-versions';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

type NativePlatform = 'ios' | 'android';

interface VersionResponse {
  version?: unknown;
  ios?: unknown;
  android?: unknown;
  version_ios?: unknown;
  version_android?: unknown;
  versions?: {
    ios?: unknown;
    android?: unknown;
  };
}

@Injectable({
  providedIn: 'root'
})

export class VersionCheckService {

  constructor(private http: HttpClient) { }

  getLatestVersion(): Observable<string | null> {
    return this.http.get<VersionResponse>(`${environment.apiUrl}/application/${environment.application}/version`)
      .pipe(
        map(response => this.extractLatestVersion(response)),
        catchError(this.handleError)
      );
  }

  getCurrentVersion(): Observable<string> {
    return from(App.getInfo()).pipe(
      map(info => this.normalizeVersion(info.version))
    );
  }

  checkVersion(): Observable<boolean> {
    return this.getLatestVersion().pipe(
      switchMap(latestVersion => {
        if (!latestVersion) {
          return of(false);
        }

        return this.getCurrentVersion().pipe(
          map(currentVersion => compareVersions(latestVersion, currentVersion) > 0)
        );
      }),
      catchError(this.handleError)
    );
  }

  private extractLatestVersion(response: VersionResponse): string | null {
    const platform = this.getCurrentNativePlatform();
    const candidateVersions = this.getVersionCandidates(response, platform);

    for (const candidate of candidateVersions) {
      const normalized = this.normalizeVersionSafely(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return null;
  }

  private getCurrentNativePlatform(): NativePlatform | null {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios' || platform === 'android') {
      return platform;
    }

    return null;
  }

  private getVersionCandidates(response: VersionResponse, platform: NativePlatform | null): unknown[] {
    const platformCandidates: unknown[] = [];

    if (platform === 'ios') {
      platformCandidates.push(
        response.versions?.ios,
        response.ios,
        response.version_ios
      );
    }

    if (platform === 'android') {
      platformCandidates.push(
        response.versions?.android,
        response.android,
        response.version_android
      );
    }

    return [
      ...platformCandidates,
      response.version
    ];
  }

  private normalizeVersionSafely(version: unknown): string | null {
    try {
      return this.normalizeVersion(version);
    } catch {
      return null;
    }
  }

  private normalizeVersion(version: unknown): string {
    if (typeof version !== 'string') {
      throw new Error('Invalid version: expected a string');
    }

    const cleaned = version
      .trim()
      .replace(/^v/i, '')
      .replace(/\+.+$/, '');

    const match = cleaned.match(/\d+(?:\.\d+){0,3}/);
    const normalized = match?.[0];

    if (!normalized) {
      throw new Error(`Invalid version: "${version}"`);
    }

    return normalized;
  }

  private handleError(error: unknown) {
    let errorMessage = 'Unknown error!';

    if (error instanceof HttpErrorResponse) {
      if (error.error instanceof ErrorEvent) {
        errorMessage = `Error: ${error.error.message}`;
      } else {
        errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      }
    } else if (error instanceof Error) {
      errorMessage = `Error: ${error.message}`;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    console.error(errorMessage);
    return throwError(errorMessage);
  }
}
